import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import {
  Dashboard,
  DashboardId,
} from '../../common/components/dashboards/types';
import {
  useDomainContext,
  useDomainViewDashboardServiceContext,
  useFullViewDashboardServiceContext,
  useUserViewDashboardServiceContext,
} from '../../context/contexts';
import { DashboardService } from './dashboard-service';
import { LocalStorageFavoriteDashboardRatingService } from './local-storage-service';

const useListFullViewDashboardsQueryKey = 'useListFullViewDashboardsQueryKey';
export const useListDomainViewDashboardsQueryKey =
  'useListDomainViewDashboardsQueryKey';
export const useListUserViewDashboardsQueryKey =
  'useListUserViewDashboardsQueryKey';
const useGetUserViewCustomDashboardConfigKey =
  'useGetUserViewCustomDashboardConfigKey';

export const useListUserViewCustomDashboardConfigs = () => {
  const dashboardService = useUserViewDashboardServiceContext();
  const { data: userViewCustomDashboardConfigsList } = useSuspenseQuery({
    queryKey: [
      'useListUserViewCustomDashboardConfigsKey',
      ...dashboardService.getDeps(),
    ],
    queryFn: async () => {
      return dashboardService.listCustomDashboardConfigs();
    },
  });
  return userViewCustomDashboardConfigsList;
};

export const useGetUserViewCustomDashboardConfig = (
  dashboardId: DashboardId
) => {
  const dashboardService = useUserViewDashboardServiceContext();
  const { data: userViewCustomDashboardConfig } = useSuspenseQuery({
    queryKey: [
      useGetUserViewCustomDashboardConfigKey,
      dashboardId,
      ...dashboardService.getDeps(),
    ],
    queryFn: async () => {
      return dashboardService.getCustomDashboardConfig(dashboardId);
    },
  });
  return userViewCustomDashboardConfig;
};

const useListDashboards = (
  dashboardService: DashboardService,
  queryKey: string
): { dashboards: Dashboard[]; isLoading: boolean } => {
  const { data: dashboards, isLoading } = useQuery({
    queryKey: [queryKey, ...dashboardService.getDeps()],
    queryFn: async () => {
      return dashboardService.listDashboards();
    },
  });
  return { dashboards: dashboards ?? [], isLoading };
};

export const useListFullViewDashboards = () => {
  const dashboardService = useFullViewDashboardServiceContext();
  return useListDashboards(dashboardService, useListFullViewDashboardsQueryKey);
};

export const useListDomainViewDashboards = () => {
  const dashboardService = useDomainViewDashboardServiceContext();
  return useListDashboards(
    dashboardService,
    useListDomainViewDashboardsQueryKey
  );
};

export const useFavoriteDashboardsRatings = () => {
  const domain = useDomainContext();
  const { data: favoriteDashboardsRatings, isLoading } = useQuery({
    queryKey: ['favorite-dashboards', domain],
    queryFn: () => {
      return LocalStorageFavoriteDashboardRatingService.get();
    },
  });
  return {
    favoriteDashboardsRatings: favoriteDashboardsRatings ?? null,
    isLoading,
  };
};

export const useListUserViewDashboards = () => {
  const dashboardService = useUserViewDashboardServiceContext();
  const { dashboards, isLoading: isDashboardsLoading } = useListDashboards(
    dashboardService,
    useListUserViewDashboardsQueryKey
  );
  const {
    favoriteDashboardsRatings,
    isLoading: isFavoriteDashboardsRatingsLoading,
  } = useFavoriteDashboardsRatings();

  const isLoading = isDashboardsLoading || isFavoriteDashboardsRatingsLoading;

  let allowedDashboards = dashboards;
  if (!isFavoriteDashboardsRatingsLoading && favoriteDashboardsRatings) {
    if (Object.keys(favoriteDashboardsRatings).length) {
      const favoriteAllowedDashboards = dashboards
        .filter((db) => favoriteDashboardsRatings[db.id])
        .sort(
          (a, b) =>
            favoriteDashboardsRatings[b.id] - favoriteDashboardsRatings[a.id]
        );

      allowedDashboards = Array.from(
        new Set([...favoriteAllowedDashboards, ...allowedDashboards])
      );
    }
  }
  return { dashboards: allowedDashboards, isLoading };
};
